import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { FloppyDisk, UploadSimple, X, MagnifyingGlass, Sun, Moon, Plus, Minus } from '@phosphor-icons/react';
import { optimizeImage } from '@/lib/imageOptimizer';
import { invalidateRevealCache } from '@/components/ImageReveal';
import { invalidateTransitionCache } from '@/components/PageTransition';
import { AppearancePreview } from './AppearancePreview';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export interface SiteSettings {
  id: string;
  font_family: string;
  font_size: number;
  font_weight: number;
  text_transform: string;
  text_decoration: string;
  text_color: string;
  link_color: string;
  hover_color: string;
  active_color: string;
  link_underline: boolean;
  link_hover_underline: boolean;
  hover_transition: string;
  menu_position_h: string;
  menu_position_v: string;
  menu_position: string;
  logo_menu_gap: number;
  menu_type: string;
  site_name: string;
  logo_url: string | null;
  logo_position: string;
  logo_size: number;
  link_bg_enabled: boolean;
  link_bg_color: string;
  custom_font_url: string | null;
  menu_bg_enabled: boolean;
  menu_bg_color: string;
  menu_border_radius: number;
  site_margin: number;
  page_transition_type: string;
  page_transition_duration: number;
  page_transition_easing: string;
  dark_mode_enabled: boolean;
  dark_bg_color: string;
  dark_text_color: string;
  dark_link_color: string;
  dark_hover_color: string;
  dark_active_color: string;
  dark_menu_bg_color: string;
  dark_link_bg_color: string;
  cursor_type: string;
  cursor_image_url: string | null;
  cursor_size: number;
  cursor_hotspot_x: number;
  cursor_hotspot_y: number;
  cursor_zoom_intensity: number;
  cursor_zoom_size: number;
  cursor_zoom_border_color: string;
  cursor_trail_enabled: boolean;
  cursor_trail_length: number;
  cursor_trail_fade_speed: number;
  cursor_trail_color: string;
  cursor_trail_opacity: number;
  image_reveal_type: string;
  image_reveal_duration: number;
  image_reveal_easing: string;
  image_reveal_stagger: number;
  image_reveal_distance: number;
  menu_item_gap: number;
  footer_links: { label: string; url: string }[] | null;
}

const M = "'IBM Plex Mono', monospace";

const GOOGLE_FONTS = [
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins',
  'Oswald', 'Raleway', 'Playfair Display', 'Merriweather',
  'Source Code Pro', 'Fira Code', 'JetBrains Mono', 'IBM Plex Mono',
  'Space Mono', 'Courier Prime', 'PT Mono', 'Inconsolata',
  'Cormorant Garamond', 'Libre Baskerville', 'EB Garamond',
  'Crimson Text', 'Spectral', 'Source Serif Pro',
  'Work Sans', 'Archivo', 'Barlow', 'DM Sans', 'Manrope', 'Outfit',
  'Space Grotesk', 'Syne', 'Clash Display',
];
const FONT_WEIGHTS = [100, 200, 300, 400, 500, 600, 700, 800, 900];
const TEXT_TRANSFORMS = [
  { value: 'none', label: 'Nenhum' },
  { value: 'uppercase', label: 'MAIÚSCULAS' },
  { value: 'lowercase', label: 'minúsculas' },
  { value: 'capitalize', label: 'Capitalizar' },
];
const TEXT_DECORATIONS = [
  { value: 'none', label: 'Nenhum' },
  { value: 'underline', label: 'Sublinhado' },
  { value: 'line-through', label: 'Riscado' },
];
const HOVER_TRANSITIONS = [
  { value: 'quickflip', label: 'Quickflip' },
  { value: 'fade', label: 'Fade' },
  { value: 'slide', label: 'Slick' },
  { value: 'scale', label: 'Scale' },
  { value: 'blur', label: 'Blur' },
  { value: 'underline-slide', label: 'Underline Slide' },
  { value: 'color-wipe', label: 'Color Wipe' },
  { value: 'glow', label: 'Glow' },
  { value: 'letter-spacing', label: 'Letter Spacing' },
  { value: 'rotate', label: 'Rotate' },
  { value: 'bounce', label: 'Bounce' },
  { value: 'skew', label: 'Drip' },
  { value: 'shadow-lift', label: 'Shadow Lift' },
  { value: 'strike', label: 'Strikethrough' },
  { value: 'background-slide', label: 'Background Slide' },
  { value: 'opacity-scale', label: 'Opacity + Scale' },
  { value: 'none', label: 'Nenhum' },
];
const MENU_TYPE_OPTIONS = [
  { value: 'horizontal', label: 'Horizontal' },
  { value: 'vertical', label: 'Vertical' },
];
const MENU_H_OPTIONS = [
  { value: 'topo', label: 'Topo' },
  { value: 'topo-esquerda', label: 'Topo Esquerda' },
  { value: 'topo-direita', label: 'Topo Direita' },
  { value: 'bottom-esquerda', label: 'Inferior Esquerda' },
  { value: 'bottom-direita', label: 'Inferior Direita' },
  { value: 'bottom', label: 'Inferior Centro' },
  { value: 'esquerda', label: 'Centro Esquerda' },
  { value: 'direita', label: 'Centro Direita' },
  { value: 'central', label: 'Central' },
  { value: 'distribute', label: 'Distribuir' },
];
const MENU_V_OPTIONS = [
  { value: 'esquerda', label: 'Esquerda' },
  { value: 'esquerda-central', label: 'Esquerda Central' },
  { value: 'esquerda-inferior', label: 'Esquerda Inferior' },
  { value: 'centro', label: 'Centro' },
  { value: 'direita', label: 'Direita' },
  { value: 'direita-inferior', label: 'Direita Inferior' },
  { value: 'distribute', label: 'Distribuir' },
];
const BORDER_RADIUS_OPTIONS = [
  { value: '0', label: '4px' },
  { value: '8', label: '8px' },
  { value: '16', label: '16px' },
  { value: '24', label: '24px' },
  { value: '40', label: '40px' },
  { value: '999', label: 'Pill' },
];
const ITEM_GAP_OPTIONS = [
  { value: '4', label: '4px' },
  { value: '8', label: '8px' },
  { value: '16', label: '16px' },
  { value: '24', label: '24px' },
  { value: '40', label: '40px' },
  { value: '64', label: 'Pill' },
];
const PAGE_TRANSITIONS = [
  { value: 'none', label: 'Nenhuma' },
  { value: 'fade', label: 'Fade' },
  { value: 'slide-left', label: 'Slide In Left' },
  { value: 'slide-right', label: 'Slide In Right' },
  { value: 'slide-up', label: 'Slide In Up' },
  { value: 'slide-down', label: 'Slide In Down' },
  { value: 'scale', label: 'Scale' },
];
const EASING_OPTIONS = [
  { value: 'ease', label: 'Ease' },
  { value: 'ease-in', label: 'Ease In' },
  { value: 'ease-out', label: 'Ease Out' },
  { value: 'ease-in-out', label: 'Ease In-Out' },
  { value: 'cubic-bezier(0.76, 0, 0.24, 1)', label: 'Custom' },
];
const IMAGE_REVEAL_OPTIONS = [
  { value: 'none', label: 'Nenhuma' },
  { value: 'fade-in', label: 'Fade In' },
  { value: 'slide-up', label: 'Slide Up' },
  { value: 'slide-down', label: 'Slide Down' },
  { value: 'slide-left', label: 'Slide Left' },
  { value: 'slide-right', label: 'Slide Right' },
  { value: 'scale-up', label: 'Scale Up' },
  { value: 'blur-in', label: 'Blow In' },
  { value: 'clip-reveal', label: 'Clip Reveal' },
  { value: 'rotate-in', label: 'Rotate In' },
  { value: 'zoom-out', label: 'Zoom Out' },
];
const CURSOR_TYPE_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'dot', label: 'Dot' },
  { value: 'ring', label: 'Ring' },
  { value: 'cross', label: 'Cross' },
  { value: 'blue-ring', label: 'Blue Ring' },
  { value: 'filled-circle', label: 'Filled Circle' },
  { value: 'loading-permanent', label: 'Loading' },
  { value: 'none', label: 'None' },
  { value: 'custom-image', label: 'Imagem' },
  { value: 'zoom', label: 'Zoom' },
];
const FONT_ACCEPT = '.woff2,.woff,.ttf,.otf';

export type SectionKey = 'tema' | 'tipografia' | 'menu' | 'transicoes' | 'cursor';

export function AdminAppearance() {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editingMode, setEditingMode] = useState<'light' | 'dark'>('light');
  const [fontSearch, setFontSearch] = useState('');
  const [fontDropdownOpen, setFontDropdownOpen] = useState(false);
  const [uploadingFont, setUploadingFont] = useState(false);
  const [uploadingCursorImg, setUploadingCursorImg] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const fontInputRef = useRef<HTMLInputElement>(null);
  const cursorImgInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const fontDropdownRef = useRef<HTMLDivElement>(null);
  const [menuItems, setMenuItems] = useState<string[]>([]);

  // All sections closed by default
  const [openSections, setOpenSections] = useState<Set<SectionKey>>(new Set());

  // Track the most recently opened section for preview
  const [activePreviewSection, setActivePreviewSection] = useState<SectionKey | null>(null);

  // Draggable column width
  const [leftWidth, setLeftWidth] = useState(520);
  const [isDragging, setIsDragging] = useState(false);
  const [dividerHover, setDividerHover] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<SectionKey, HTMLDivElement | null>>({
    tema: null, tipografia: null, menu: null, transicoes: null, cursor: null,
  });

  const toggleSection = useCallback((key: SectionKey) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        // If closing the active preview section, find another or set null
        if (activePreviewSection === key) {
          const remaining = Array.from(next);
          setActivePreviewSection(remaining.length > 0 ? remaining[remaining.length - 1] : null);
        }
      } else {
        next.add(key);
        setActivePreviewSection(key);
        setTimeout(() => {
          sectionRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      }
      return next;
    });
  }, [activePreviewSection]);

  // Drag logic for column resize
  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newWidth = Math.min(Math.max(e.clientX - rect.left, 320), rect.width * 0.7);
      setLeftWidth(newWidth);
    };
    const handleMouseUp = () => setIsDragging(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging]);

  useEffect(() => {
    const families = GOOGLE_FONTS.map(f => f.replace(/ /g, '+')).join('&family=');
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${families}&display=swap`;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      let { data } = await supabase.from('site_settings').select('*').eq('user_id', user.id).limit(1).maybeSingle();
      if (!data) {
        const { data: created } = await supabase.from('site_settings').insert({ user_id: user.id } as any).select('*').single();
        data = created;
      }
      if (data) {
        const raw = data as any;
        const legacyMap: Record<string, string> = {
          'topo-esquerda': 'top-left', 'topo': 'top-center',
          'topo-direita': 'top-right', 'esquerda': 'center-left',
          'esquerda-central': 'center-left', 'esquerda-inferior': 'bottom-left',
          'centro': 'center', 'direita': 'center-right',
          'direita-inferior': 'bottom-right', 'bottom': 'bottom-center',
          'bottom-esquerda': 'bottom-left', 'bottom-direita': 'bottom-right',
        };
        const validPos = new Set(['top-left','top-center','top-right','center-left','center','center-right','bottom-left','bottom-center','bottom-right']);
        let mp: string = raw.menu_position;
        if (!mp || !validPos.has(mp)) {
          mp = legacyMap[raw.menu_position_v] || legacyMap[raw.menu_position_h] || legacyMap[mp] || 'top-left';
        }
        const s: SiteSettings = {
          ...(raw as SiteSettings),
          menu_position: mp,
          logo_menu_gap: typeof raw.logo_menu_gap === 'number' ? raw.logo_menu_gap : 16,
        };
        setSettings(s);
        setEditingMode(s.dark_mode_enabled ? 'dark' : 'light');
      }
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    const loadMenu = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from('menu_items')
        .select('label')
        .eq('user_id', user.id)
        .eq('visible', true)
        .order('sort_order');
      if (data) setMenuItems(data.map((d: any) => d.label));
    };
    loadMenu();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (fontDropdownRef.current && !fontDropdownRef.current.contains(e.target as Node)) {
        setFontDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const update = (key: keyof Omit<SiteSettings, 'id'>, value: string | number | boolean | null) => {
    if (!settings) return;
    setSettings({ ...settings, [key]: value });
    setSaved(false);
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    const { id, ...rest } = settings;
    await supabase.from('site_settings').update({ ...rest, updated_at: new Date().toISOString() } as any).eq('id', id);
    invalidateTransitionCache();
    invalidateRevealCache();
    window.dispatchEvent(new CustomEvent('appearance-saved'));
    window.dispatchEvent(new CustomEvent('cms-dark-mode-change', {
      detail: { 
        dark_mode_enabled: settings.dark_mode_enabled, 
        dark_bg_color: settings.dark_bg_color, 
        dark_text_color: settings.dark_text_color 
      },
    }));
    window.dispatchEvent(new CustomEvent('cms-appearance-saved', { detail: settings }));
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleFontUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !settings) return;
    setUploadingFont(true);
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    if (!userId) { setUploadingFont(false); return; }
    const ext = file.name.split('.').pop()?.toLowerCase();
    const path = `${userId}/fonts/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('site-assets').upload(path, file, {
      contentType: ext === 'woff2' ? 'font/woff2' : ext === 'woff' ? 'font/woff' : ext === 'ttf' ? 'font/ttf' : 'font/otf',
    });
    if (!error) {
      const { data } = supabase.storage.from('site-assets').getPublicUrl(path);
      update('custom_font_url', data.publicUrl);
      update('font_family', file.name.replace(/\.[^.]+$/, ''));
    }
    setUploadingFont(false);
    e.target.value = '';
  };

  const handleCursorImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !settings) return;
    setUploadingCursorImg(true);
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;
    if (!userId) { setUploadingCursorImg(false); return; }
    const result = await optimizeImage(file);
    const ext = result.file.name.split('.').pop();
    const path = `${userId}/cursors/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('site-assets').upload(path, result.file);
    if (!error) {
      const { data } = supabase.storage.from('site-assets').getPublicUrl(path);
      update('cursor_image_url', data.publicUrl);
    }
    setUploadingCursorImg(false);
    e.target.value = '';
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !settings) return;
    setLogoError(null);
    setUploadingLogo(true);

    const acceptedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    const maxSize = 10 * 1024 * 1024;

    if (!acceptedTypes.includes(file.type)) {
      setLogoError('Formato não suportado. Use PNG, JPG, SVG ou WEBP.');
      setUploadingLogo(false);
      e.target.value = '';
      return;
    }

    if (file.size > maxSize) {
      setLogoError('Arquivo muito grande. Máximo de 10MB.');
      setUploadingLogo(false);
      e.target.value = '';
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;
      if (!userId) {
        setLogoError('Não foi possível enviar a logo. Tente novamente.');
        return;
      }
      const result = await optimizeImage(file);
      const ext = result.file.name.split('.').pop();
      const path = `${userId}/logos/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('site-assets').upload(path, result.file);
      if (error) {
        setLogoError('Não foi possível enviar a logo. Tente novamente.');
        return;
      }
      const { data } = supabase.storage.from('site-assets').getPublicUrl(path);
      update('logo_url', data.publicUrl);
    } catch (err) {
      setLogoError('Não foi possível enviar a logo. Tente novamente.');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const filteredFonts = GOOGLE_FONTS.filter(f => f.toLowerCase().includes(fontSearch.toLowerCase()));

  if (loading || !settings) return <p style={{ fontFamily: M, color: 'rgba(247,246,242,0.5)' }}>Carregando...</p>;

  const isDarkEdit = editingMode === 'dark';

  const lightPalette = [
    { label: 'Fundo', key: 'dark_bg_color' as const, lightKey: null as any, lightDefault: '#F7F6F2' },
    { label: 'Texto', key: 'dark_text_color' as const, lightKey: 'text_color' as const },
    { label: 'Link', key: 'dark_link_color' as const, lightKey: 'link_color' as const },
    { label: 'Hover', key: 'dark_hover_color' as const, lightKey: 'hover_color' as const },
    { label: 'Ativo', key: 'dark_active_color' as const, lightKey: 'active_color' as const },
    { label: 'Borda', key: 'dark_menu_bg_color' as const, lightKey: 'menu_bg_color' as const },
  ];

  const getSwatchColor = (idx: number): string => {
    const p = lightPalette[idx];
    if (isDarkEdit) return (settings as any)[p.key] || '#000000';
    if (p.lightKey) return (settings as any)[p.lightKey] || '#000000';
    return p.lightDefault || '#F7F6F2';
  };

  const setSwatchColor = (idx: number, val: string) => {
    const p = lightPalette[idx];
    if (isDarkEdit) {
      update(p.key, val);
    } else {
      if (p.lightKey) update(p.lightKey, val);
    }
  };

  const typoColors = isDarkEdit
    ? [
        { label: 'Texto', value: settings.dark_text_color, set: (v: string) => update('dark_text_color', v) },
        { label: 'Link', value: settings.dark_link_color, set: (v: string) => update('dark_link_color', v) },
        { label: 'Hover', value: settings.dark_hover_color, set: (v: string) => update('dark_hover_color', v) },
        { label: 'Ativo', value: settings.dark_active_color, set: (v: string) => update('dark_active_color', v) },
      ]
    : [
        { label: 'Texto', value: settings.text_color, set: (v: string) => update('text_color', v) },
        { label: 'Link', value: settings.link_color, set: (v: string) => update('link_color', v) },
        { label: 'Hover', value: settings.hover_color, set: (v: string) => update('hover_color', v) },
        { label: 'Ativo', value: settings.active_color, set: (v: string) => update('active_color', v) },
      ];

  return (
    <div ref={containerRef} style={{ display: 'flex', flexDirection: 'row', width: '100%', height: '100%', overflow: 'hidden', position: 'relative' }}>

      {/* LEFT COLUMN — Controls */}
      <div style={{ width: leftWidth, flexShrink: 0, height: '100%', display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
        <div ref={scrollContainerRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '32px 40px 0 40px' }}>

          {/* TEMA — with sun/moon in header */}
          <div ref={el => { sectionRefs.current.tema = el; }} data-section="tema">
            <TooltipProvider delayDuration={300}>
              <button
                onClick={() => toggleSection('tema')}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  width: '100%', padding: '16px 0',
                  background: 'none', border: 'none', cursor: 'pointer',
                  borderBottom: openSections.has('tema') ? 'none' : '1px solid rgba(247,246,242,0.08)',
                }}
              >
                <span style={{
                  fontFamily: M, fontSize: 12, fontWeight: 300, letterSpacing: '0.1em',
                  textTransform: 'uppercase', color: 'rgba(247,246,242,0.5)',
                }}>
                  Tema
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setEditingMode('light'); 
                          update('dark_mode_enabled', false);
                          window.dispatchEvent(new CustomEvent('cms-dark-mode-change', {
                            detail: { 
                              dark_mode_enabled: false, 
                              dark_bg_color: settings.dark_bg_color, 
                              dark_text_color: settings.dark_text_color 
                            },
                          }));
                        }}
                        style={{ cursor: 'pointer', display: 'flex', padding: 2 }}
                      >
                        <Sun size={16} weight="thin" style={{ color: 'rgba(247,246,242,0.85)', opacity: !isDarkEdit ? 1 : 0.4, transition: 'opacity 0.2s ease' }} />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-[11px]">Modo claro</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setEditingMode('dark'); 
                          update('dark_mode_enabled', true);
                          window.dispatchEvent(new CustomEvent('cms-dark-mode-change', {
                            detail: { 
                              dark_mode_enabled: true, 
                              dark_bg_color: settings.dark_bg_color, 
                              dark_text_color: settings.dark_text_color 
                            },
                          }));
                        }}
                        style={{ cursor: 'pointer', display: 'flex', padding: 2 }}
                      >
                        <Moon size={16} weight="thin" style={{ color: 'rgba(247,246,242,0.85)', opacity: isDarkEdit ? 1 : 0.4, transition: 'opacity 0.2s ease' }} />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-[11px]">Modo escuro</TooltipContent>
                  </Tooltip>
                  <span style={{ width: 1, height: 16, backgroundColor: 'rgba(247,246,242,0.2)' }} />
                  {openSections.has('tema')
                    ? <Minus size={16} weight="thin" style={{ color: 'rgba(247,246,242,0.4)' }} />
                    : <Plus size={16} weight="thin" style={{ color: 'rgba(247,246,242,0.4)' }} />
                  }
                </span>
              </button>
            </TooltipProvider>
            <AccordionBody isOpen={openSections.has('tema')}>
              {/* Site name */}
              <Field label="Nome do site">
                <input
                  value={settings.site_name}
                  onChange={(e) => update('site_name', e.target.value)}
                  style={inputS}
                  placeholder="CRIAAA"
                />
              </Field>

              {/* Logo upload */}
              <Field label="Logo">
                <div className="flex items-center gap-3 mb-2">
                  {settings.logo_url && (
                    <div className="relative">
                      <img src={settings.logo_url} alt="Logo" className="h-10 object-contain" style={{ border: '0.5px solid rgba(247,246,242,0.15)' }} />
                      <button onClick={() => update('logo_url', null)} className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center" style={{ backgroundColor: 'rgba(247,246,242,0.85)', color: '#0A0A0A', fontSize: 12 }}><X size={10} /></button>
                    </div>
                  )}
                  <button
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                    style={{ fontFamily: M, fontSize: 12, textTransform: 'uppercase', padding: '8px 12px', border: '0.5px solid rgba(247,246,242,0.15)', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.6)' }}
                  >
                    <UploadSimple size={12} weight="thin" className="inline mr-1" />
                    {uploadingLogo ? '...' : settings.logo_url ? 'Trocar' : 'Enviar Logo'}
                  </button>
                  <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={handleLogoUpload} />
                </div>
                <p style={{ fontFamily: M, fontSize: 12, fontWeight: 300, color: 'rgba(247,246,242,0.4)' }}>
                  PNG ou SVG, fundo transparente. Altura recomendada: 400–800px. Máx. 10MB.
                </p>
                {logoError && (
                  <p style={{ fontFamily: M, fontSize: 12, fontWeight: 300, color: '#FF3F2D', marginTop: 4 }}>
                    {logoError}
                  </p>
                )}
              </Field>

              {/* Logo size */}
              <Field label={`Tamanho do logo (${settings.logo_size || 80}px)`}>
                <div className="flex items-center gap-3">
                  <span style={{ fontFamily: M, fontSize: 12, color: 'rgba(247,246,242,0.4)' }}>40</span>
                  <input
                    type="range"
                    min={40}
                    max={300}
                    value={settings.logo_size || 80}
                    onChange={(e) => update('logo_size', parseInt(e.target.value))}
                    style={{ flex: 1, accentColor: 'rgba(247,246,242,0.6)' }}
                  />
                  <span style={{ fontFamily: M, fontSize: 12, color: 'rgba(247,246,242,0.4)' }}>300</span>
                </div>
              </Field>

              {/* Logo position */}
              <Field label="Posição do logo">
                <Chips
                  options={[
                    { value: 'top-left', label: 'Topo Esq.' },
                    { value: 'top-center', label: 'Topo Centro' },
                    { value: 'top-right', label: 'Topo Dir.' },
                    { value: 'center-left', label: 'Centro Esq.' },
                    { value: 'center-right', label: 'Centro Dir.' },
                    { value: 'bottom-left', label: 'Inf. Esq.' },
                    { value: 'bottom-center', label: 'Inf. Centro' },
                    { value: 'bottom-right', label: 'Inf. Dir.' },
                  ]}
                  value={settings.logo_position}
                  onChange={(v) => update('logo_position', v)}
                />
              </Field>

              {/* Color swatches */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 8 }}>
                {lightPalette.map((p, i) => (
                  <div key={p.label} className="flex flex-col items-center gap-1">
                    <label htmlFor={`swatch-${i}`} style={{ cursor: 'pointer' }}>
                      <div style={{
                        width: 40, height: 40,
                        backgroundColor: getSwatchColor(i),
                        border: '0.5px solid rgba(247,246,242,0.15)',
                        cursor: 'pointer',
                        position: 'relative',
                      }}>
                        <input
                          id={`swatch-${i}`}
                          type="color"
                          value={getSwatchColor(i)}
                          onChange={(e) => setSwatchColor(i, e.target.value)}
                          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}
                        />
                      </div>
                    </label>
                    <span style={{ fontFamily: M, fontSize: 12, color: 'rgba(247,246,242,0.5)', fontWeight: 300 }}>{p.label}</span>
                  </div>
                ))}
              </div>
            </AccordionBody>
          </div>

          {/* TIPOGRAFIA */}
          <div ref={el => { sectionRefs.current.tipografia = el; }} data-section="tipografia">
            <AccordionHeader label="Tipografia" isOpen={openSections.has('tipografia')} onToggle={() => toggleSection('tipografia')} />
            <AccordionBody isOpen={openSections.has('tipografia')}>
              {/* Font selector */}
              <div className="flex items-center gap-2 mb-4">
                <div className="flex-1 relative" ref={fontDropdownRef}>
                  <div
                    className="flex items-center gap-2 cursor-pointer"
                    style={{
                      fontFamily: M, fontSize: 13, fontWeight: 300, padding: '6px 10px',
                      border: '0.5px solid rgba(247,246,242,0.15)', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.85)',
                    }}
                    onClick={() => setFontDropdownOpen(!fontDropdownOpen)}
                  >
                    <span style={{ fontFamily: `'${settings.font_family}', sans-serif`, flex: 1 }}>{settings.font_family}</span>
                    <MagnifyingGlass size={12} weight="thin" style={{ opacity: 0.4 }} />
                  </div>
                  {fontDropdownOpen && (
                    <div className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto" style={{ border: '0.5px solid rgba(247,246,242,0.15)', backgroundColor: 'rgba(247,246,242,0.06)' }}>
                      <div className="sticky top-0 p-2" style={{ backgroundColor: 'rgba(247,246,242,0.06)' }}>
                        <input
                          value={fontSearch}
                          onChange={(e) => setFontSearch(e.target.value)}
                          placeholder="Buscar fonte..."
                          aria-label="Buscar fonte por nome"
                          style={{ fontFamily: M, fontSize: 13, fontWeight: 300, padding: '6px 10px', border: '0.5px solid rgba(247,246,242,0.15)', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.85)', width: '100%' }}
                          autoFocus
                        />
                      </div>
                      {filteredFonts.map(font => (
                        <button
                          key={font}
                          onClick={() => { update('font_family', font); setFontDropdownOpen(false); setFontSearch(''); }}
                          className="w-full text-left px-3 py-2"
                          style={{
                            fontFamily: `'${font}', sans-serif`, fontSize: 14, color: 'rgba(247,246,242,0.85)',
                            backgroundColor: settings.font_family === font ? 'rgba(247,246,242,0.06)' : 'transparent',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(247,246,242,0.04)')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = settings.font_family === font ? 'rgba(247,246,242,0.06)' : 'transparent')}
                        >
                          {font}
                          <span className="block text-[10px]" style={{ fontFamily: M, opacity: 0.4 }}>The quick brown fox</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => fontInputRef.current?.click()}
                  disabled={uploadingFont}
                  style={{
                    fontFamily: M, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
                    padding: '8px 12px', border: '0.5px solid rgba(247,246,242,0.15)',
                    backgroundColor: 'transparent', color: 'rgba(247,246,242,0.6)',
                  }}
                >
                  <UploadSimple size={12} weight="thin" className="inline mr-1" />
                  {uploadingFont ? '...' : 'Upload'}
                </button>
                <input ref={fontInputRef} type="file" accept={FONT_ACCEPT} className="hidden" onChange={handleFontUpload} />
              </div>

              <div className="grid grid-cols-3 gap-3 mb-4">
                <Field label="Tamanho (px)">
                  <input type="number" value={settings.font_size} onChange={(e) => update('font_size', parseInt(e.target.value) || 16)} min={8} max={72} style={inputS} />
                </Field>
                <Field label="Peso">
                  <select value={settings.font_weight} onChange={(e) => update('font_weight', parseInt(e.target.value))} style={inputS}>
                    {FONT_WEIGHTS.map(w => <option key={w} value={w}>{w}</option>)}
                  </select>
                </Field>
                <Field label="Capitalização">
                  <select value={settings.text_transform} onChange={(e) => update('text_transform', e.target.value)} style={inputS}>
                    {TEXT_TRANSFORMS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
              </div>

              <Field label="Decoração">
                <Chips options={TEXT_DECORATIONS} value={settings.text_decoration} onChange={(v) => update('text_decoration', v)} />
              </Field>

              <div className="flex gap-6 mt-4 mb-4">
                {typoColors.map(c => (
                  <div key={c.label} className="flex flex-col items-center gap-1">
                    <div style={{ position: 'relative', width: 32, height: 32, backgroundColor: c.value, border: '0.5px solid rgba(247,246,242,0.15)', cursor: 'pointer' }}>
                      <input type="color" value={c.value} onChange={(e) => c.set(e.target.value)} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }} />
                    </div>
                    <span style={{ fontFamily: M, fontSize: 12, color: 'rgba(247,246,242,0.5)', fontWeight: 300 }}>{c.label}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-4 mb-4 flex-wrap">
                <Checkbox label="Underline" checked={settings.link_underline} onChange={(v) => update('link_underline', v)} />
                <Checkbox label="Hover underline" checked={settings.link_hover_underline} onChange={(v) => update('link_hover_underline', v)} />
                <Checkbox label="Background no link" checked={settings.link_bg_enabled} onChange={(v) => update('link_bg_enabled', v)} />
              </div>

              <Field label="Transição do hover">
                <Chips options={HOVER_TRANSITIONS} value={settings.hover_transition} onChange={(v) => update('hover_transition', v)} />
              </Field>
            </AccordionBody>
          </div>

          {/* MENU */}
          <div ref={el => { sectionRefs.current.menu = el; }} data-section="menu">
            <AccordionHeader label="Menu" isOpen={openSections.has('menu')} onToggle={() => toggleSection('menu')} />
            <AccordionBody isOpen={openSections.has('menu')}>
              <Field label="Orientação">
                <Chips options={MENU_TYPE_OPTIONS} value={settings.menu_type} onChange={(v) => update('menu_type', v)} />
              </Field>
              <Field label="Posição do menu">
                <MenuPositionGrid
                  value={settings.menu_position}
                  onChange={(v) => update('menu_position', v)}
                />
              </Field>
              {settings.logo_position === settings.menu_position && (
                <Field label={`Espaço entre logo e menu (${settings.logo_menu_gap ?? 16}px)`}>
                  <input
                    type="number"
                    min={0}
                    max={200}
                    value={settings.logo_menu_gap ?? 16}
                    onChange={(e) => update('logo_menu_gap', parseInt(e.target.value) || 0)}
                    style={inputS}
                  />
                </Field>
              )}
              <Checkbox label="Background no menu" checked={settings.menu_bg_enabled} onChange={(v) => update('menu_bg_enabled', v)} />
              <Field label="Espaço entre itens">
                <Chips options={ITEM_GAP_OPTIONS} value={String(settings.menu_item_gap ?? 24)} onChange={(v) => update('menu_item_gap', parseInt(v))} />
              </Field>
            </AccordionBody>
          </div>

          {/* TRANSIÇÕES */}
          <div ref={el => { sectionRefs.current.transicoes = el; }} data-section="transicoes">
            <AccordionHeader label="Transições" isOpen={openSections.has('transicoes')} onToggle={() => toggleSection('transicoes')} />
            <AccordionBody isOpen={openSections.has('transicoes')}>
              <Field label="Entre páginas">
                <Chips options={PAGE_TRANSITIONS} value={settings.page_transition_type} onChange={(v) => update('page_transition_type', v)} />
              </Field>
              {settings.page_transition_type !== 'none' && (
                <>
                  <Field label={`Duração: ${settings.page_transition_duration}ms`}>
                    <div className="flex items-center gap-3">
                      <input type="range" min={200} max={1200} step={50} value={settings.page_transition_duration} onChange={(e) => update('page_transition_duration', parseInt(e.target.value))} style={{ flex: 1, accentColor: 'rgba(247,246,242,0.6)' }} />
                      <span style={{ fontFamily: M, fontSize: 12, color: 'rgba(247,246,242,0.5)', fontWeight: 300, minWidth: 48 }}>{settings.page_transition_duration}ms</span>
                    </div>
                  </Field>
                  <Field label="Easing">
                    <Chips options={EASING_OPTIONS} value={settings.page_transition_easing} onChange={(v) => update('page_transition_easing', v)} />
                  </Field>
                </>
              )}
              <Field label="Animação das imagens">
                <Chips options={IMAGE_REVEAL_OPTIONS} value={settings.image_reveal_type} onChange={(v) => update('image_reveal_type', v)} />
              </Field>
            </AccordionBody>
          </div>

          {/* CURSOR */}
          <div ref={el => { sectionRefs.current.cursor = el; }} data-section="cursor">
            <AccordionHeader label="Cursor" isOpen={openSections.has('cursor')} onToggle={() => toggleSection('cursor')} />
            <AccordionBody isOpen={openSections.has('cursor')}>
              <Field label="Tipo de cursor">
                <Chips options={CURSOR_TYPE_OPTIONS} value={settings.cursor_type} onChange={(v) => update('cursor_type', v)} />
              </Field>

              {settings.cursor_type === 'custom-image' && (
                <div className="flex items-center gap-3 mb-3">
                  {settings.cursor_image_url && (
                    <div className="relative">
                      <img src={settings.cursor_image_url} alt="Cursor" className="h-10 object-contain" style={{ imageRendering: 'pixelated' }} />
                      <button onClick={() => update('cursor_image_url', null)} className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center" style={{ backgroundColor: 'rgba(247,246,242,0.85)', color: '#0A0A0A', fontSize: 12 }}><X size={10} /></button>
                    </div>
                  )}
                  <button
                    onClick={() => cursorImgInputRef.current?.click()}
                    disabled={uploadingCursorImg}
                    style={{ fontFamily: M, fontSize: 12, textTransform: 'uppercase', padding: '8px 12px', border: '0.5px solid rgba(247,246,242,0.15)', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.6)' }}
                  >
                    <UploadSimple size={12} weight="thin" className="inline mr-1" />
                    {uploadingCursorImg ? '...' : 'Enviar Imagem'}
                  </button>
                  <input ref={cursorImgInputRef} type="file" accept=".png,.svg,.gif,.cur" className="hidden" onChange={handleCursorImageUpload} />
                </div>
              )}

              <Checkbox label="Adicionar rastro ao cursor" checked={settings.cursor_trail_enabled} onChange={(v) => update('cursor_trail_enabled', v)} />
              {settings.cursor_trail_enabled && (
                <div className="grid grid-cols-2 gap-4 mt-3 mb-4">
                  <Field label={`Comprimento: ${settings.cursor_trail_length}`}>
                    <input type="range" min={3} max={20} value={settings.cursor_trail_length} onChange={(e) => update('cursor_trail_length', parseInt(e.target.value))} style={{ width: '100%', accentColor: 'rgba(247,246,242,0.6)' }} />
                  </Field>
                  <Field label={`Dissipação: ${settings.cursor_trail_fade_speed}ms`}>
                    <input type="range" min={100} max={800} step={50} value={settings.cursor_trail_fade_speed} onChange={(e) => update('cursor_trail_fade_speed', parseInt(e.target.value))} style={{ width: '100%', accentColor: 'rgba(247,246,242,0.6)' }} />
                  </Field>
                  <div className="flex flex-col items-start gap-1">
                    <span style={{ fontFamily: M, fontSize: 12, textTransform: 'uppercase', color: 'rgba(247,246,242,0.4)', fontWeight: 300 }}>Cor do rastro</span>
                    <div style={{ position: 'relative', width: 32, height: 32, backgroundColor: settings.cursor_trail_color, border: '0.5px solid rgba(247,246,242,0.15)', cursor: 'pointer' }}>
                      <input type="color" value={settings.cursor_trail_color} onChange={(e) => update('cursor_trail_color', e.target.value)} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }} />
                    </div>
                  </div>
                  <Field label={`Opacidade: ${Math.round(settings.cursor_trail_opacity * 100)}%`}>
                    <input type="range" min={0.1} max={0.8} step={0.05} value={settings.cursor_trail_opacity} onChange={(e) => update('cursor_trail_opacity', parseFloat(e.target.value))} style={{ width: '100%', accentColor: 'rgba(247,246,242,0.6)' }} />
                  </Field>
                </div>
              )}
            </AccordionBody>
          </div>

          <div style={{ height: 80 }} />
        </div>

        {/* STICKY SAVE BAR */}
        <div style={{
          padding: '16px 40px',
          borderTop: '0.5px solid rgba(247,246,242,0.08)',
          backgroundColor: '#0A0A0A',
        }}>
          <button
            onClick={save}
            disabled={saving}
            style={{
              width: '100%', fontFamily: M, fontSize: 13, fontWeight: 300, textTransform: 'uppercase',
              padding: '12px 0', border: '0.5px solid rgba(247,246,242,0.2)',
              backgroundColor: 'transparent', color: 'rgba(247,246,242,0.85)',
              cursor: saving ? 'wait' : 'pointer', letterSpacing: '0.05em',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'opacity 0.2s',
              opacity: saving ? 0.5 : 1,
            }}
          >
            <FloppyDisk size={14} weight="thin" />
            {saving ? 'Salvando...' : saved ? 'SALVO ✓' : 'SALVAR APARÊNCIA'}
          </button>
        </div>
      </div>

      {/* DRAG HANDLE */}
      <div
        onMouseDown={() => setIsDragging(true)}
        onMouseEnter={() => setDividerHover(true)}
        onMouseLeave={() => setDividerHover(false)}
        style={{
          width: 4,
          cursor: 'col-resize',
          backgroundColor: isDragging || dividerHover ? 'rgba(247,246,242,0.15)' : 'transparent',
          transition: 'background-color 0.15s ease',
          flexShrink: 0,
        }}
      />

      {/* RIGHT COLUMN — Contextual Preview */}
      <div style={{ flex: 1, minWidth: 0, height: '100%', overflow: 'hidden', padding: 32 }}>
        <AppearancePreview
          settings={settings}
          editingMode={editingMode}
          menuItems={menuItems}
          activeSection={activePreviewSection}
        />
      </div>
    </div>
  );
}

/* ── Accordion components ── */

function AccordionHeader({ label, isOpen, onToggle }: { label: string; isOpen: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        width: '100%', padding: '16px 0',
        background: 'none', border: 'none', cursor: 'pointer',
        borderBottomWidth: isOpen ? 0 : 1,
        borderBottomStyle: 'solid',
        borderBottomColor: 'rgba(247,246,242,0.08)',
      }}
    >
      <span style={{
        fontFamily: M, fontSize: 12, fontWeight: 300, letterSpacing: '0.1em',
        textTransform: 'uppercase', color: 'rgba(247,246,242,0.5)',
      }}>
        {label}
      </span>
      {isOpen
        ? <Minus size={16} weight="thin" style={{ color: 'rgba(247,246,242,0.4)' }} />
        : <Plus size={16} weight="thin" style={{ color: 'rgba(247,246,242,0.4)' }} />
      }
    </button>
  );
}

function AccordionBody({ isOpen, children }: { isOpen: boolean; children: React.ReactNode }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      const observer = new ResizeObserver(() => {
        if (contentRef.current) setHeight(contentRef.current.scrollHeight);
      });
      observer.observe(contentRef.current);
      return () => observer.disconnect();
    }
  }, [isOpen]);

  return (
    <div style={{
      maxHeight: isOpen ? height || 2000 : 0,
      opacity: isOpen ? 1 : 0,
      overflow: 'hidden',
      transition: 'max-height 0.25s ease, opacity 0.25s ease',
    }}>
      <div ref={contentRef} style={{ paddingBottom: isOpen ? 24 : 0 }}>
        {children}
      </div>
    </div>
  );
}

/* ── Shared sub-components ── */

const inputS: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: 13, fontWeight: 300, padding: '6px 10px',
  border: '0.5px solid rgba(247,246,242,0.15)',
  backgroundColor: 'transparent', color: 'rgba(247,246,242,0.85)',
  width: '100%',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 mb-3">
      <label style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
        color: 'rgba(247,246,242,0.4)',
      }}>{label}</label>
      {children}
    </div>
  );
}

function Chips({ options, value, onChange }: { options: { value: string; label: string }[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {options.map(o => (
        <button
          key={o.value + o.label}
          onClick={() => onChange(o.value)}
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
            padding: '4px 8px',
            border: '0.5px solid',
            borderColor: value === o.value ? 'rgba(247,246,242,0.85)' : 'rgba(247,246,242,0.15)',
            backgroundColor: value === o.value ? 'rgba(247,246,242,0.85)' : 'transparent',
            color: value === o.value ? '#0A0A0A' : 'rgba(247,246,242,0.5)',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button onClick={() => onChange(!checked)} className="flex items-center gap-2" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 300, textTransform: 'uppercase', color: 'rgba(247,246,242,0.6)' }}>
      <span style={{ width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '0.5px solid rgba(247,246,242,0.15)', fontSize: 12, color: 'rgba(247,246,242,0.85)' }}>
        {checked ? '✓' : ''}
      </span>
      {label}
    </button>
  );
}

function MenuPositionGrid({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const positions: string[][] = [
    ['top-left', 'top-center', 'top-right'],
    ['center-left', 'center', 'center-right'],
    ['bottom-left', 'bottom-center', 'bottom-right'],
  ];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 32px)',
        gridTemplateRows: 'repeat(3, 32px)',
        gap: 4,
      }}
    >
      {positions.flat().map((pos) => {
        const active = value === pos;
        return (
          <button
            key={pos}
            type="button"
            aria-label={pos}
            aria-pressed={active}
            onClick={() => onChange(pos)}
            style={{
              width: 32, height: 32,
              border: `1px solid ${active ? 'rgba(247,246,242,0.85)' : 'rgba(247,246,242,0.15)'}`,
              backgroundColor: 'transparent',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 0,
            }}
          >
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              backgroundColor: active ? 'rgba(247,246,242,0.85)' : 'transparent',
              border: active ? 'none' : '1px solid rgba(247,246,242,0.35)',
            }} />
          </button>
        );
      })}
    </div>
  );
}
